import { useEffect, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useSession } from './contexts/SessionContext';
import { ClerkSessionProvider } from './auth/ClerkSessionProvider';
import { TENANT_SLUG, useTenantConfig } from './auth/useTenantConfig';
import SsoCallback from './auth/SsoCallback';
import { SignInPage, SignUpPage } from './auth/ClerkAuthPages';
import WelcomeView from './views/WelcomeView';
import LobbyView from './views/LobbyView';
import { LOBBY_QUERY_KEY } from './api/useLobby';
import './App.css';

const ENV_CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Health() {
  return (
    <main className="min-h-screen bg-background text-foreground p-6">
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card p-6">
        <h2 className="text-2xl font-semibold">OK</h2>
        <p className="text-muted-foreground">Routing + React Query + Tailwind are configured.</p>
      </div>
    </main>
  );
}

function LoadingScreen() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#161616]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E02125] mx-auto mb-4"></div>
        <p className="text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
          {t('app.loading')}
        </p>
      </div>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#161616]">
      <div className="text-center max-w-md px-6">
        <div className="text-red-500 text-5xl mb-4">⚠️</div>
        <h2
          className="text-white text-xl font-bold mb-2"
          style={{ fontFamily: '"Saira Extra Condensed", sans-serif' }}
        >
          {t('app.somethingWentWrong')}
        </h2>
        <p className="text-gray-400 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
          {message}
        </p>
      </div>
    </div>
  );
}

/**
 * Boots the Clerk auth flow (same shape as the pickem frontend): the tenant
 * config supplies the publishable key, and ClerkSessionProvider exchanges the
 * Clerk session for the internal JWT. Fixture mode skips auth entirely.
 */
function AuthGate({ children }: { children: ReactNode }) {
  const { data: tenant, isLoading, error } = useTenantConfig();

  if (!TENANT_SLUG) {
    return <ErrorScreen message="VITE_TENANT_SLUG is not configured" />;
  }
  if (isLoading) return <LoadingScreen />;
  if (error) return <ErrorScreen message="Failed to load tenant configuration" />;
  if (tenant?.authProvider !== 'clerk') {
    return <ErrorScreen message="Tenant is not configured for Clerk authentication" />;
  }

  const publishableKey = tenant.clerkPublishableKey || ENV_CLERK_PUBLISHABLE_KEY;
  if (!publishableKey) {
    return <ErrorScreen message="Clerk publishable key is not configured for this tenant" />;
  }
  return <ClerkSessionProvider publishableKey={publishableKey}>{children}</ClerkSessionProvider>;
}

function AppContent() {
  const state = useSession();

  if (state.status === 'loading') return <LoadingScreen />;
  if (state.status === 'error') return <ErrorScreen message={state.message} />;

  return (
    <BrowserRouter>
      <AnimatedRoutes />
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

function AnimatedRoutes() {
  const { t } = useTranslation();
  const location = useLocation();
  const state = useSession();
  // Key on the top-level segment so nested routes don't unmount their parent
  // view mid-navigation.
  const topLevelKey = '/' + (location.pathname.split('/')[1] ?? '');
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black">
      <div className="relative w-full h-full max-w-[440px] overflow-hidden transform-gpu">
        <AnimatePresence mode="sync" initial={false}>
          <Routes location={location} key={topLevelKey}>
            <Route path="/" element={<WelcomeView />} />
            <Route
              path="/lobby/*"
              element={state.status === 'ready' ? <LobbyView /> : <Navigate to="/" replace />}
            />
            {!!TENANT_SLUG && <Route path="/sign-in/*" element={<SignInPage />} />}
            {!!TENANT_SLUG && <Route path="/sign-up/*" element={<SignUpPage />} />}
            {!!TENANT_SLUG && <Route path="/sso-callback" element={<SsoCallback />} />}
            <Route path="/health" element={<Health />} />
            <Route path="*" element={<ErrorScreen message={t('app.pageNotFound')} />} />
          </Routes>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.source !== 'simulator') return;
      if (event.data?.type === 'refetch-lobby') {
        queryClient.invalidateQueries({ queryKey: LOBBY_QUERY_KEY });
      }
      if (event.data?.type === 'reload') {
        window.location.reload();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthGate>
        <AppContent />
      </AuthGate>
    </QueryClientProvider>
  );
}
